import { defineStore } from 'pinia';
import { err } from 'neverthrow';
import { unpackRules } from '@casl/ability/extra';
import { computed, ref } from '@vue/composition-api';
import { HTTPResultAsync } from '@/utils/http';
import { SignUpRequest } from '@/modules/user/dto/SignUp';
import { SignUpInvitedRequest } from '@/modules/user/dto/SignUpInvited';
import { ResetPasswordRequest } from '@/modules/user/dto/ResetPassword';
import { CreateCompanyRequest } from '@/modules/company/dto/CreateCompany';
import { SendResetPasswordEmailRequest } from '@/modules/user/dto/SendResetPasswordEmail';
import { SignInRequest } from '@/modules/user/dto/SignIn';
import { VerifyRequest } from '@/modules/user/dto/Verify';
import { VerifyPwdResetTokenRequest } from '@/modules/user/dto/VerifyToken';
import * as AuthService from '@/modules/user/AuthService';
import * as UserService from '@/modules/user/UserService';
import * as CompanyService from '@/modules/company/CompanyService';
import { UpdateProfileRequest } from '@/modules/user/dto/UpdateProfile';
import { SetPasswordRequest } from '@/modules/user/dto/SetPassword';
import IUser from '@/modules/user/IUser';
import { Set2FARequest } from '@/modules/user/dto/Set2FA';
import { ability } from '@/utils/ability';

export const useAuthStore = defineStore('auth', () => {
  const loggedInUser = ref<IUser | undefined>(undefined);

  const loggedInUserError = ref<Error | undefined>(undefined);

  const loggedInUserState = ref<string>('ANONYMOUS');

  const loggedInUserFetching = ref<boolean>(false);

  const isLoggedIn = computed<boolean>(() => !!(!loggedInUserError.value && loggedInUser.value && loggedInUser.value._id !== null));

  const loggedInUserSafe = computed(() => (isLoggedIn.value ? loggedInUser.value : undefined));

  const TRY_RESUMING_KEY = 'auth:tryResuming';

  async function refreshLoggedInUser(isAuto = false) {
    loggedInUserState.value = isAuto ? 'LOADING_AUTO' : 'LOADING_MANUAL';
    loggedInUserFetching.value = true;
    const result = await AuthService.me();

    loggedInUserFetching.value = false;
    if (result.isOk()) {
      localStorage.setItem(TRY_RESUMING_KEY, 'true');
      loggedInUser.value = result.value.payload.user;
      ability.update(unpackRules(result.value.payload.authRules));
      loggedInUserError.value = undefined;
      loggedInUserState.value = isAuto ? 'IN_RESUMED' : 'IN_MANUAL';
    } else {
      loggedInUser.value = undefined;
      loggedInUserError.value = result.error;
      loggedInUserState.value = 'ERROR';
      localStorage.removeItem(TRY_RESUMING_KEY);
    }
    return result;
  }

  async function resetLoggedInUser() {
    localStorage.removeItem(TRY_RESUMING_KEY);
    loggedInUser.value = undefined;
    loggedInUserError.value = undefined;
  }

  async function submitSignUp(payload: SignUpRequest): HTTPResultAsync {
    const response = await UserService.signUp(payload);
    if (response.isOk()) {
      await refreshLoggedInUser();
    }
    return response;
  }

  async function submitSignUpInvited(payload: SignUpInvitedRequest): HTTPResultAsync {
    const response = await CompanyService.signUpInvited(payload);
    if (response.isOk()) {
      await refreshLoggedInUser();
    }
    return response;
  }

  async function submitSignIn(payload: SignInRequest) {
    const result = await AuthService.signIn(payload);
    if (result.isOk()) {
      await refreshLoggedInUser();
    }
    return result;
  }

  async function submitIssueVerificationCode() {
    return AuthService.issueVerificationCode();
  }

  async function submitSignOut() {
    const result = await AuthService.signOut();
    if (result.isOk()) {
      resetLoggedInUser();
    }
    return result;
  }

  async function submitVerifyPwdResetToken(payload: VerifyPwdResetTokenRequest): HTTPResultAsync {
    return AuthService.verifyToken(payload);
  }

  async function submitVerify(payload: VerifyRequest): HTTPResultAsync {
    const result = await AuthService.verify(payload);
    if (result.isOk()) {
      await refreshLoggedInUser();
    }
    return result;
  }

  async function submitSendResetPasswordEmail(payload: SendResetPasswordEmailRequest): HTTPResultAsync {
    return AuthService.sendResetPasswordEmail(payload);
  }

  async function submitSendUnlockAccountEmail(payload: SendResetPasswordEmailRequest): HTTPResultAsync {
    return AuthService.sendUnlockAccountEmail(payload);
  }

  async function submitResetPassword(payload: ResetPasswordRequest): HTTPResultAsync {
    return AuthService.resetPassword(payload);
  }

  async function submitCompany(payload: CreateCompanyRequest, idempotencyKey: string): HTTPResultAsync {
    const result = await CompanyService.create({ ...payload, onboarding: '1' }, idempotencyKey);
    if (result.isOk()) {
      await refreshLoggedInUser();
    }
    return result;
  }

  async function submitUpdateProfile(payload: UpdateProfileRequest): HTTPResultAsync {
    if (loggedInUser?.value) {
      const result = await UserService.updateProfile(loggedInUser.value._id, payload);
      if (result.isOk()) {
        await refreshLoggedInUser();
      }
      return result;
    }
    resetLoggedInUser();
    return err(new Error('Session timed out.'));
  }

  async function submitSetPassword(payload: SetPasswordRequest): HTTPResultAsync {
    if (loggedInUser?.value) {
      // _id: loggedInUser?.value?._id
      const res = await AuthService.setPassword({ ...payload });
      await submitSignOut();
      return res;
    }
    resetLoggedInUser();
    return err(new Error('Session timed out.'));
  }

  async function submitVerifyInviteToken(token: string): HTTPResultAsync {
    const result = await CompanyService.verifyInviteToken(token);
    return result;
  }

  async function submit2FAInitiation(): HTTPResultAsync {
    const result = await AuthService.initiate2FA();
    return result;
  }

  async function submit2FAEnable(payload: Set2FARequest): HTTPResultAsync {
    const result = await AuthService.enable2FA(payload);
    if (result.isOk()) {
      await refreshLoggedInUser();
    }
    return result;
  }

  async function submit2FADisable(payload: Set2FARequest): HTTPResultAsync {
    const result = await AuthService.disable2FA(payload);
    if (result.isOk()) {
      await refreshLoggedInUser();
    }
    return result;
  }

  // if (localStorage.getItem(TRY_RESUMING_KEY)) {
  //   // if logged in before, try fetching user info
  //   refreshLoggedInUser(true)
  //     .then(() => {
  //       if (!isLoggedIn) {
  //         submitSignOut();
  //       }
  //     })
  //     .catch(() => {
  //       submitSignOut();
  //     });
  // }

  return {
    loggedInUser: loggedInUserSafe,
    loggedInUserError,
    loggedInUserState,
    isLoggedIn,
    submitSignUp,
    submitSignUpInvited,
    submitSignIn,
    submitSignOut,
    submitVerify,
    submitSendResetPasswordEmail,
    submitSendUnlockAccountEmail,
    submitResetPassword,
    submitCompany,
    submitUpdateProfile,
    submitSetPassword,
    submitVerifyPwdResetToken,
    submitIssueVerificationCode,
    submitVerifyInviteToken,
    submit2FAInitiation,
    submit2FAEnable,
    submit2FADisable,
    refreshLoggedInUser,
  };
}, { persist: true });
